import React, { useEffect, useRef, useState } from 'react';
import BetTicketHeader from './BetTicketHeader';
import BetTicketBallSelection from './BetTicketBallSelection';
import BetTicketStakeSection from './BetTicketStakeSection';
import { useTicketEditor } from './hooks/useTicketEditor';
import { useAssistantActions } from '../../hooks/useLottoAssistantContext';
import { formatBalance } from '../../utils/common';
import classes from './BetTicket.module.scss';

const BetTicket = ({ ticket, onTicketUpdate, onInfoClick, onRemoveTicket }) => {
  const ticketRef = useRef(null);
  const [isBallDrawerOpen, setIsBallDrawerOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const addedTimeoutRef = useRef(null);
  const { trackLottoEvent } = useAssistantActions();

  useEffect(
    () => () => {
      if (addedTimeoutRef.current) clearTimeout(addedTimeoutRef.current);
    },
    []
  );

  const editor = useTicketEditor({ ticket, onTicketUpdate, trackLottoEvent });
  const hasBalls = ticket.balls != null && ticket.balls.length > 0;

  const handleInfoClick = (event) => {
    event.stopPropagation();
    if (onInfoClick && ticket.draw_id) onInfoClick(ticket.draw_id);
  };

  const handleCloseClick = (event) => {
    event.stopPropagation();
    if (onRemoveTicket && ticket.ticket_id) onRemoveTicket(ticket.ticket_id);
  };

  const handleAddToCart = () => {
    if (hasBalls && editor.state.balls.some((b) => Number.isNaN(b.value))) {
      setIsBallDrawerOpen(true);
      return;
    }
    editor.handlers.handleAddToCart(ticketRef);

    if (addedTimeoutRef.current) clearTimeout(addedTimeoutRef.current);
    setJustAdded(true);
    addedTimeoutRef.current = setTimeout(() => setJustAdded(false), 2500);
  };

  return (
    <div ref={ticketRef} className={classes.Container}>
      <BetTicketHeader
        ticket={ticket}
        hasBalls={hasBalls}
        onInfoClick={handleInfoClick}
        onRemoveTicket={onRemoveTicket ? handleCloseClick : undefined}
      />

      <hr className={classes.Divider} />

      <div className={classes.Body}>
        {hasBalls ? (
          <>
            <h4 className={classes.SectionLabel}>YOUR NUMBERS</h4>
            <BetTicketBallSelection
              ticket={ticket}
              balls={editor.state.balls}
              onBallsChange={editor.actions.setBalls}
              onValidationError={editor.actions.setValidationError}
              isDrawerOpen={isBallDrawerOpen}
              onDrawerOpen={() => setIsBallDrawerOpen(true)}
              onDrawerClose={() => setIsBallDrawerOpen(false)}
            />
          </>
        ) : (
          <>
            <h4 className={classes.SectionLabel}>YOUR PREDICTION</h4>
            <div className={classes.OutcomeContainer}>
              <p className={classes.OutcomeText}>{ticket.market_title}</p>
              <p className={classes.OutcomeText}>
                Outcome:{' '}
                <span className={classes.OutcomeBold}>
                  {ticket.outcome?.outcome_title}
                </span>
              </p>
            </div>
          </>
        )}
      </div>

      <hr className={classes.Divider} />

      <BetTicketStakeSection
        ticket={ticket}
        state={editor.state}
        derived={editor.derived}
        actions={editor.actions}
      />

      <div className={classes.ConfirmContainer}>
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={!editor.validation.isStakeValid || justAdded}
          className={classes.ConfirmButton + (justAdded ? ' ' + classes.JustAdded : '')}
        >
          {justAdded
            ? 'Added'
            : `Add to Cart ${formatBalance(editor.derived.totalCost * 100, 'ZAR')}`}
        </button>

        {editor.validation.addToCartError && (
          <p className={classes.ErrorText}>{editor.validation.addToCartError}</p>
        )}
      </div>
    </div>
  );
};

export default BetTicket;
